import React from 'react';
import { Mail, Phone, Instagram, Linkedin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { lawyerData } from '../data/mock';

const HEADER_OFFSET = 96;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior: 'smooth' });
  return true;
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const quickLinks = [
    { label: 'Início', href: '/#inicio' },
    { label: 'Conheça a Dra.', href: '/#sobre' },
    { label: 'Áreas de Atuação', href: '/#areas' },
    { label: 'Diferenciais', href: '/#diferenciais' },
    { label: 'Depoimentos', href: '/#depoimentos' },
    { label: 'Contato', href: '/#analise' },
  ];

  const goToLink = (href) => {
    const hashIndex = href.indexOf('#');
    if (hashIndex !== -1) {
      const id = href.slice(hashIndex + 1);
      if (location.pathname === '/') {
        scrollToId(id);
        window.history.replaceState(null, '', `/#${id}`);
      } else {
        navigate(`/#${id}`);
      }
      return;
    }

    navigate(href);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <footer className="bg-[#1a1a1a] text-white border-t border-[#d4af37]/20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5 sm:mb-6">
              <img
                src={lawyerData.logo || '/assets/logo-de-paula-silva.png'}
                alt={`${lawyerData.firmName || 'De Paula & Silva'}`}
                className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <p className="font-serif text-sm font-semibold text-white truncate">{lawyerData.firmName || lawyerData.name}</p>
                <p className="text-[10px] sm:text-xs text-[#d4af37] tracking-wider uppercase leading-snug">
                  <span className="block">Advocacia, Assessoria e</span>
                  <span className="block">Consultoria Jurídica</span>
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">Links Rápidos</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <button
                    type="button"
                    onClick={() => goToLink(link.href)}
                    className="text-white/70 hover:text-[#d4af37] text-sm transition-colors duration-300 text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">Contato</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <p className="text-white/70 text-sm">{lawyerData.phone}</p>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <p className="text-white/70 text-sm">{lawyerData.email}</p>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">Horário de Atendimento</h4>
            <p className="text-white/70 text-sm mb-6">{lawyerData.hours}</p>
            <h4 className="font-semibold text-white mb-4">Redes Sociais</h4>
            <div className="flex gap-3">
              <a
                href={lawyerData.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#d4af37] hover:text-[#1a1a1a] transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={lawyerData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#d4af37] hover:text-[#1a1a1a] transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">
              &copy; {currentYear} {lawyerData.firmName || lawyerData.name}. Todos os direitos reservados.
            </p>
            <p className="text-white/50 text-xs text-center md:text-right max-w-xl">
              Este site possui caráter meramente informativo, não constituindo solicitação de clientela,
              conforme disposições do Código de Ética e Disciplina da OAB.
            </p>
          </div>
          <p className="text-white/40 text-xs text-center mt-4">
            Desenvolvido por{' '}
            <a
              href="https://softwaresecure.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d4af37] hover:text-[#e5c65c] transition-colors duration-300"
            >
              Software Secure
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
